import React, { useCallback, useState } from 'react';
import QRCode from 'qrcode';
import { Loader2, Mail } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../../../../components/ui/dialog';
import { Notification as customNotification } from '../../../../../../components/ui/custom-notification';
import pipefitterApi from '../../../../../../services/pipefitterApi';
import type { PipeSize1 } from '../../../../types/setup';
import {
  MAX_QR_PAYLOAD_BYTES,
  buildPipeQRPayload,
  formatPipeQRText,
  type PipeQRPayload,
} from './pipeSizeQRPayload';

export interface UsePipeSizeQRCodeOptions {
  projectNumber?: string | null;
  projectName?: string | null;
  pipeSizes: PipeSize1[];
  /** Data360 project fields for email body (start/end dates, location, etc.). */
  projectInfo?: {
    startDate?: string | null;
    endDate?: string | null;
    location?: string | null;
    customerName?: string | null;
    targetNumberOfPipes?: number | null;
  };
}

export function usePipeSizeQRCode({
  projectNumber,
  projectName,
  pipeSizes,
  projectInfo,
}: UsePipeSizeQRCodeOptions) {
  const [open, setOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [payload, setPayload] = useState<PipeQRPayload | null>(null);
  const [label, setLabel] = useState('All pipes');
  const [email, setEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [allPipesEmailOpen, setAllPipesEmailOpen] = useState(false);
  const [allPipesEmail, setAllPipesEmail] = useState('');
  const [allPipesEmailSending, setAllPipesEmailSending] = useState(false);

  const authUserEmail = useSelector(
    (state: { auth?: { user?: { email?: string } } }) =>
      state.auth?.user?.email?.trim() ?? ''
  );

  const buildApiPayload = useCallback(
    (pipeIndex?: number) =>
      buildPipeQRPayload({
        projectNumber,
        projectName,
        customerName: projectInfo?.customerName,
        location: projectInfo?.location,
        pipeSizes,
        pipeIndex,
      }),
    [pipeSizes, projectNumber, projectName, projectInfo]
  );

  const buildProjectInfoPayload = useCallback(
    (payload: PipeQRPayload) => {
      const pipe = payload.pipes[0];
      return {
        projectName: projectName || payload.projectName || '',
        startDate: projectInfo?.startDate || null,
        endDate: projectInfo?.endDate || null,
        location: projectInfo?.location || null,
        customerName: projectInfo?.customerName || null,
        hiLoThreshold: pipe?.hiloThreshold ?? null,
        nominalOD: pipe?.nominalOd ?? null,
        exclusion6OClock: pipe?.sixOclockExclusionDeg ?? null,
        targetNumberOfPipes:
          pipe?.targetNumberOfPipes ?? projectInfo?.targetNumberOfPipes ?? null,
        pipeType: pipe?.pipeType ?? null,
      };
    },
    [projectName, projectInfo]
  );

  const generateAndShow = useCallback(
    async (pipeIndex?: number) => {
      if (!pipeSizes.length) {
        customNotification.warning(
          'No pipe data',
          'Add at least one pipe size to generate QR code.'
        );
        return;
      }
      if (
        pipeIndex != null &&
        (pipeIndex < 0 || pipeIndex >= pipeSizes.length)
      ) {
        customNotification.warning(
          'Invalid pipe',
          'Select a valid pipe to generate QR code.'
        );
        return;
      }

      setGenerating(true);
      setQrCodeUrl('');
      setEmail('');
      setPayload(null);
      setLabel(pipeIndex != null ? `Pipe ${pipeIndex + 1}` : 'All pipes');
      setOpen(true);

      try {
        const nextPayload = buildApiPayload(pipeIndex);
        // Slim plain text for scan (email PDF still uses full qrPayload).
        const qrText = formatPipeQRText(nextPayload);
        if (qrText.length > MAX_QR_PAYLOAD_BYTES) {
          const n = nextPayload.pipes.length;
          const perPipe = n ? Math.round(qrText.length / n) : 200;
          const maxPipes = Math.max(
            1,
            Math.floor(MAX_QR_PAYLOAD_BYTES / Math.max(perPipe, 100))
          );
          if (pipeIndex == null && n > 1) {
            setPayload(nextPayload);
            setQrCodeUrl('');
            customNotification.warning(
              'Combined QR too large',
              `Preview QR is not shown (${qrText.length} characters). You can still email the all-pipes PDF with one QR per pipe.`
            );
            return;
          }
          customNotification.error(
            'QR code data too large',
            `Data is ${qrText.length} characters (max ${MAX_QR_PAYLOAD_BYTES}). Reduce to ${maxPipes} pipe(s) or fewer.`
          );
          setQrCodeUrl('');
          return;
        }

        const dataUrl = await QRCode.toDataURL(qrText, {
          width: 400,
          margin: 2,
          errorCorrectionLevel: 'L',
        });
        setPayload(nextPayload);
        setQrCodeUrl(dataUrl);
      } catch (err) {
        console.error('[Pipe QR] Error:', err);
        customNotification.error(
          'QR Code',
          err instanceof Error
            ? err.message
            : 'Failed to generate QR code. Try again.'
        );
        setQrCodeUrl('');
      } finally {
        setGenerating(false);
      }
    },
    [buildApiPayload]
  );

  const showAll = useCallback(() => generateAndShow(), [generateAndShow]);
  const showPipe = useCallback(
    (index: number) => generateAndShow(index),
    [generateAndShow]
  );

  const sendPipeQrEmail = useCallback(
    async (trimmed: string, emailPayload: PipeQRPayload) => {
      const response = await pipefitterApi.post('/project/email-pipe-qr', {
        emails: [trimmed],
        projectNumber: emailPayload.projectNumber || projectNumber || '',
        projectName: emailPayload.projectName || projectName || '',
        projectInfo: buildProjectInfoPayload(emailPayload),
        qrPayload: emailPayload,
      });
      if (response?.data?.status && response.data.status !== 200) {
        throw new Error(response.data.message || 'Failed to send email');
      }
      return emailPayload.pipes.length;
    },
    [projectNumber, projectName, buildProjectInfoPayload]
  );

  const openAllPipesEmailModal = useCallback(() => {
    if (!pipeSizes.length) {
      customNotification.warning(
        'No pipe data',
        'Add at least one pipe size to email all pipe QR codes.'
      );
      return;
    }
    setAllPipesEmail(authUserEmail);
    setAllPipesEmailOpen(true);
  }, [pipeSizes.length, authUserEmail]);

  const sendAllPipesEmail = useCallback(async () => {
    const trimmed = allPipesEmail.trim();
    if (!trimmed) {
      customNotification.error(
        'Email required',
        'Enter an email address to receive the all-pipes QR PDF.'
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      customNotification.error('Invalid email', 'Enter a valid email address.');
      return;
    }

    setAllPipesEmailSending(true);
    try {
      const emailPayload = buildApiPayload();
      const pipeCount = await sendPipeQrEmail(trimmed, emailPayload);
      customNotification.success(
        'Email sent',
        `All pipe QR report (${pipeCount} pipes) is being sent to ${trimmed}. Check your inbox for the PDF.`
      );
      setAllPipesEmailOpen(false);
    } catch (err: any) {
      customNotification.error(
        'Email failed',
        err?.response?.data?.message ||
          err?.message ||
          'Could not send all-pipes QR email.'
      );
    } finally {
      setAllPipesEmailSending(false);
    }
  }, [allPipesEmail, buildApiPayload, sendPipeQrEmail]);

  const handleSendEmail = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      customNotification.error(
        'Email required',
        'Enter an email address to share the QR code.'
      );
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      customNotification.error('Invalid email', 'Enter a valid email address.');
      return;
    }
    if (!payload?.pipes?.length) {
      customNotification.error(
        'No QR data',
        'Generate the QR code before sending email.'
      );
      return;
    }

    setEmailSending(true);
    try {
      const pipeCount = await sendPipeQrEmail(trimmed, payload);
      customNotification.success(
        'Email sent',
        pipeCount > 1
          ? `All pipe QR report (${pipeCount} pipes) sent to ${trimmed}.`
          : `Pipe QR report sent to ${trimmed}.`
      );
      setEmail('');
    } catch (err: any) {
      customNotification.error(
        'Email failed',
        err?.response?.data?.message ||
          err?.message ||
          'Could not send pipe QR email.'
      );
    } finally {
      setEmailSending(false);
    }
  }, [email, payload, sendPipeQrEmail]);

  const handleDownload = useCallback(() => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    const safeLabel = label.replace(/\s+/g, '-').toLowerCase();
    link.download = `pipe-qr-${projectNumber || 'setup'}-${safeLabel}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    customNotification.success(
      'Download Started',
      'QR code is being downloaded...'
    );
  }, [qrCodeUrl, label, projectNumber]);

  const modal = (
    <PipeSizeQRCodeModal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setEmail('');
          setEmailSending(false);
        }
      }}
      label={label}
      generating={generating}
      qrCodeUrl={qrCodeUrl}
      canShare={!!payload?.pipes?.length}
      email={email}
      onEmailChange={setEmail}
      emailSending={emailSending}
      onSendEmail={handleSendEmail}
      onDownload={handleDownload}
    />
  );

  const allPipesEmailModal = (
    <Dialog
      open={allPipesEmailOpen}
      onOpenChange={(next) => {
        setAllPipesEmailOpen(next);
        if (!next) setAllPipesEmailSending(false);
      }}
    >
      <DialogContent className="max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Email All Pipe QR Codes
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-gray-600">
            A PDF with one QR code page per pipe will be generated and emailed.
            This may take a moment for multiple pipes.
          </p>
          <Input
            type="email"
            value={allPipesEmail}
            onChange={(e) => setAllPipesEmail(e.target.value)}
            placeholder="name@example.com"
            disabled={allPipesEmailSending}
            className="bg-white border-[#E2E5EA]"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void sendAllPipesEmail();
              }
            }}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={allPipesEmailSending}
            onClick={() => setAllPipesEmailOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={allPipesEmailSending || !allPipesEmail.trim()}
            onClick={() => void sendAllPipesEmail()}
          >
            {allPipesEmailSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              'Send email'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return {
    generating,
    allPipesEmailSending,
    showAll,
    showPipe,
    openAllPipesEmailModal,
    modal,
    allPipesEmailModal,
  };
}

interface PipeSizeQRCodeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  generating: boolean;
  qrCodeUrl: string;
  canShare: boolean;
  email: string;
  onEmailChange: (value: string) => void;
  emailSending: boolean;
  onSendEmail: () => void;
  onDownload: () => void;
}

export const PipeSizeQRCodeModal: React.FC<PipeSizeQRCodeModalProps> = ({
  open,
  onOpenChange,
  label,
  generating,
  qrCodeUrl,
  canShare,
  email,
  onEmailChange,
  emailSending,
  onSendEmail,
  onDownload,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" aria-describedby={undefined}>
      <div className="p-4">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Pipe Size QR Code{label ? `-${label}` : ''}
          </DialogTitle>
        </DialogHeader>
        {generating ? (
          <div className="flex flex-col items-center justify-center py-12 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#e04530]" />
            <p className="text-sm text-gray-500">Generating QR code...</p>
          </div>
        ) : canShare ? (
          <div className="flex flex-col items-center justify-center space-y-4 mt-4">
            {qrCodeUrl ? (
              <>
            <p className="text-xs text-gray-500 text-center">
              {label === 'All pipes'
                ? 'Scan shows all pipe details combined. Email sends a PDF with one QR code page per pipe and project information.'
                : 'Scan shows project number/name, customer, group ID, pipe length, pipe OD, wall thickness, material, and location.'}
            </p>
            <div className="bg-white p-4 rounded-lg shadow-md border border-gray-200">
              <img
                src={qrCodeUrl}
                alt="Pipe Size QR Code"
                className="w-full h-full object-contain max-w-[256px] max-h-[256px]"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5RUiBDb2RlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                }}
              />
            </div>
            <Button
              onClick={onDownload}
            >
              Download QR Code
            </Button>
              </>
            ) : (
              <p className="text-sm text-gray-600 text-center px-2">
                Combined preview QR is not available for this many pipes. Use{' '}
                <strong>Share by email</strong> or <strong>Email All Pipe QR</strong>{' '}
                to get one QR page per pipe in the PDF.
              </p>
            )}

            <div className="w-full border-t border-gray-200 pt-4 space-y-3">
              <p className="text-[13px] font-medium text-gray-900 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blueprint-blue" />
                Share by email
              </p>
             
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="name@example.com"
                  disabled={emailSending}
                  className="bg-white border-[#E2E5EA]"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onSendEmail();
                    }
                  }}
                />
                <Button
                //  type="button"
                //  className="bg-blueprint-blue hover:bg-blueprint-blue/90 text-white shrink-0"
                  disabled={emailSending || !email.trim()}
                  onClick={onSendEmail}
                >
                  {emailSending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send email'
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-gray-500">
              No QR code available. Add pipe sizes and try again.
            </p>
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);

export default PipeSizeQRCodeModal;
